import ResourceManager from "./ResourceManager";
import { getImageUrl } from "@/lib/api";

export function AdminNews() {
  return (
    <ResourceManager
      title="Berita"
      description="Kelola artikel, kabar terbaru, dan pengumuman."
      publicList="/news"
      adminBase="/admin/news"
      fields={[
        { name: "title", label: "Judul", required: true },
        { name: "slug", label: "Slug (URL)", required: true, help: "Contoh: peluncuran-program-baru" },
        { name: "category", label: "Kategori", type: "select", options: ["Berita", "Program", "Kegiatan", "Kolaborasi", "Pengumuman"] },
        { name: "excerpt", label: "Ringkasan", type: "textarea", rows: 3 },
        { name: "content", label: "Konten Lengkap", type: "richtext", placeholder: "Tulis konten berita di sini..." },
        { name: "cover_image", label: "Cover Image", type: "image" },
        { name: "author", label: "Penulis" },
        { name: "tags", label: "Tags (satu per baris)", type: "list", rows: 3 },
        { name: "published", label: "Terpublikasi", type: "boolean", default: true },
        { name: "featured", label: "Featured", type: "boolean" },
      ]}
      listColumns={[
        { key: "title", label: "Judul" },
        { key: "category", label: "Kategori" },
        { key: "author", label: "Penulis" },
        { key: "published", label: "Status", render: (r) => r.published ? <span className="text-emerald-600 font-semibold">Live</span> : <span className="text-slate-400">Draft</span> },
        { key: "published_at", label: "Tanggal", render: (r) => r.published_at ? new Date(r.published_at).toLocaleDateString("id-ID") : "—" },
      ]}
      emptyText="Belum ada berita. Klik Tambah Baru."
    />
  );
}

export function AdminGallery() {
  return (
    <ResourceManager
      title="Galeri"
      description="Kelola foto dan video kegiatan."
      publicList="/gallery"
      adminBase="/admin/gallery"
      fields={[
        { name: "title", label: "Judul", required: true },
        { name: "category", label: "Kategori", placeholder: "Kegiatan, Kemanusiaan, dst." },
        { name: "media_type", label: "Tipe Media", type: "select", options: ["image", "video"], default: "image" },
        { name: "media_url", label: "Media", required: true, type: "image", uploadLabel: "Upload Gambar/Video" },
        { name: "thumbnail", label: "Thumbnail (untuk video)", type: "image" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 3 },
      ]}
      listColumns={[
        { key: "media_url", label: "Preview", render: (r) => r.media_url ? <img src={getImageUrl(r.thumbnail || r.media_url)} alt="" className="h-12 w-16 rounded-lg object-cover" /> : "—" },
        { key: "title", label: "Judul" },
        { key: "category", label: "Kategori" },
        { key: "media_type", label: "Tipe" },
      ]}
      emptyText="Belum ada item galeri."
    />
  );
}

export function AdminPrograms() {
  return (
    <ResourceManager
      title="Program"
      description="Kelola program strategis Sauyunan Foundation."
      publicList="/programs"
      adminBase="/admin/programs"
      listParams={{ published: false }}
      fields={[
        { name: "title", label: "Nama Program", required: true },
        { name: "slug", label: "Slug", required: true },
        { name: "category", label: "Kategori" },
        { name: "short_description", label: "Deskripsi Singkat", type: "textarea", rows: 2 },
        { name: "description", label: "Deskripsi Lengkap", type: "richtext", placeholder: "Tulis deskripsi program lengkap di sini..." },
        { name: "hero_image", label: "Hero Image", type: "image" },
        { name: "icon", label: "Ikon", type: "select", options: ["heart-handshake", "wheat", "stethoscope", "graduation-cap", "leaf", "coins", "lightbulb"] },
        { name: "color", label: "Warna Aksen", type: "select", options: [{ value: "primary", label: "Blue" }, { value: "accent", label: "Orange" }] },
        { name: "sub_programs", label: "Sub-Program (satu per baris)", type: "list", rows: 5 },
        { name: "objectives", label: "Tujuan (satu per baris)", type: "list", rows: 4 },
        { name: "activities", label: "Aktivitas (satu per baris)", type: "list", rows: 4 },
        { name: "order", label: "Urutan", type: "number" },
        { name: "published", label: "Terpublikasi", type: "boolean", default: true },
      ]}
      listColumns={[
        { key: "order", label: "#" },
        { key: "title", label: "Nama" },
        { key: "category", label: "Kategori" },
        { key: "sub_programs", label: "Sub", render: (r) => (r.sub_programs?.length || 0) },
        { key: "published", label: "Status", render: (r) => r.published ? <span className="text-emerald-600 font-semibold">Live</span> : <span className="text-slate-400">Draft</span> },
      ]}
    />
  );
}

export function AdminPublications() {
  return (
    <ResourceManager
      title="Publikasi"
      description="Kelola laporan tahunan, riset, whitepaper, dan brosur."
      publicList="/publications"
      adminBase="/admin/publications"
      fields={[
        { name: "title", label: "Judul", required: true },
        { name: "category", label: "Kategori", type: "select", options: ["Annual Report", "Financial Report", "Research", "Whitepaper", "Brochure"] },
        { name: "year", label: "Tahun", type: "number", default: new Date().getFullYear() },
        { name: "file_url", label: "File PDF", type: "file", accept: "application/pdf", uploadLabel: "Upload PDF" },
        { name: "cover_image", label: "Cover Image", type: "image" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 3 },
        { name: "published", label: "Terpublikasi", type: "boolean", default: true },
      ]}
      listColumns={[
        { key: "title", label: "Judul" },
        { key: "category", label: "Kategori" },
        { key: "year", label: "Tahun" },
      ]}
    />
  );
}

export function AdminPartners() {
  return (
    <ResourceManager
      title="Mitra"
      description="Kelola daftar mitra strategis."
      publicList="/partners"
      adminBase="/admin/partners"
      fields={[
        { name: "name", label: "Nama Mitra", required: true },
        { name: "category", label: "Kategori", type: "select", options: ["Pemerintah Pusat", "Pemerintah Daerah", "BUMN", "Swasta", "Filantropi", "Akademisi", "Internasional", "Komunitas"] },
        { name: "logo_url", label: "Logo", type: "image" },
        { name: "website", label: "Website" },
        { name: "order", label: "Urutan", type: "number" },
      ]}
      listColumns={[
        { key: "logo_url", label: "Logo", render: (r) => r.logo_url ? <img src={getImageUrl(r.logo_url)} alt="" className="h-8 w-8 object-contain" /> : "—" },
        { key: "name", label: "Nama" },
        { key: "category", label: "Kategori" },
      ]}
    />
  );
}

export function AdminTeam() {
  return (
    <ResourceManager
      title="Tim"
      description="Kelola anggota tim & kepengurusan."
      publicList="/team"
      adminBase="/admin/team"
      fields={[
        { name: "name", label: "Nama", required: true },
        { name: "role", label: "Jabatan" },
        { name: "photo_url", label: "Foto", type: "image" },
        { name: "bio", label: "Bio Singkat", type: "textarea", rows: 3 },
        { name: "linkedin", label: "LinkedIn URL" },
        { name: "order", label: "Urutan", type: "number" },
      ]}
      listColumns={[
        { key: "photo_url", label: "Foto", render: (r) => r.photo_url ? <img src={getImageUrl(r.photo_url)} alt="" className="h-10 w-10 rounded-full object-cover" /> : "—" },
        { key: "name", label: "Nama" },
        { key: "role", label: "Jabatan" },
      ]}
    />
  );
}
